import documentVersionsService from '../service/documentVersions.service';
import { markupVersionNotice } from './documentVersions';
import { error as debugError } from '@/utils/debugLogger';
import { EventBus, DOCUMENT_EVENTS } from '@/event-bus';

// Covers every realistic document; a markup version beyond it still gets a generic message.
const VERSIONS_PAGE = 100;
let requestSeq = 0;

// Switching between a document's pages or markups relaunches the viewer each time; reuse the
// versions list briefly instead of requesting it on every launch.
const VERSIONS_CACHE_MS = 60 * 1000;
// lower-cased documentId -> { at, promise } (promise of the versions array)
const versionsCache = new Map();
const cacheKey = (documentId) => String(documentId).toLowerCase();

// A new version, pin or revert changes the list.
EventBus.$on(DOCUMENT_EVENTS.DOCUMENT_CHANGED, (document) => {
  const id = document?.id || document?.documentId;
  if (id) versionsCache.delete(cacheKey(id));
});

function getVersionsCached(documentId) {
  const key = cacheKey(documentId);
  const cached = versionsCache.get(key);
  if (cached && Date.now() - cached.at < VERSIONS_CACHE_MS) return cached.promise;
  const entry = { at: Date.now(), promise: null };
  entry.promise = documentVersionsService
    .getVersions(documentId, { page: 1, itemsPerPage: VERSIONS_PAGE })
    .then((res) => res.data?.items || [])
    .catch((err) => {
      // Do not remember a failure.
      if (versionsCache.get(key) === entry) versionsCache.delete(key);
      throw err;
    });
  versionsCache.set(key, entry);
  return entry.promise;
}

/**
 * A document's versions, newest first, from the same short-lived cache the notice uses, so the
 * viewer's version switcher and the banner share one request per document.
 * @param {string} documentId
 * @returns {Promise<Array>} DocumentVersionVMs
 */
export function getDocumentVersions(documentId) {
  return getVersionsCached(documentId);
}

/** Clears the notice and ignores any request still in flight, e.g. when the viewer is closed. */
export function cancelDocumentVersionNotice(store) {
  requestSeq++;
  store.commit('SET_DOCUMENT_VERSION_NOTICE', null);
}

/**
 * Works out whether the document opened in the main PDF viewer shows a non-current version,
 * or carries markups drawn on one, and commits the result to SET_DOCUMENT_VERSION_NOTICE
 * (DOCUMENT_VERSIONS_UI_PLAN.md Phase D). Legacy attachments without a version get none.
 *
 * @param {Object} store - the Vuex store (passed in to avoid an import cycle with forgeviewer)
 * @param {Object} attachment - the ForgeAttachment being launched
 */
export async function refreshDocumentVersionNotice(store, attachment) {
  const seq = ++requestSeq;
  store.commit('SET_DOCUMENT_VERSION_NOTICE', null);

  const documentId = attachment?.referenceDocumentId;
  const markupRevisionId = attachment?.documentRevisionId || null;
  const viewingRevisionId = attachment?.viewRevisionId || null;
  if (!documentId || (!markupRevisionId && !viewingRevisionId)) return;

  try {
    const versions = await getVersionsCached(documentId);
    if (seq !== requestSeq) return; // another document was opened meanwhile
    const notice = markupVersionNotice({ markupRevisionId, viewingRevisionId, versions });
    // The markups' version is opened from its own file URL when the banner's button is used.
    const markupStorageReference = versions.find((v) => v.id === notice?.markupRevisionId)?.storageReference || null;
    store.commit('SET_DOCUMENT_VERSION_NOTICE', notice ? { ...notice, documentId, markupStorageReference } : null);
  } catch (err) {
    // A reference id that is not a document (e.g. an Origin page) or no View permission: no banner.
    debugError('[DocumentVersionNotice]', 'Could not load versions:', err);
  }
}
